/**
 * Stream A — A87-A89: AI Agent Definitions, Skills, Routing, Memory, Instruction Packs
 *
 * Control plane tables: agent_definitions, skill_library, model_routing_config, instruction_packs
 * Tenant DB tables: ai_entity_relationships, ai_agent_memory
 */
import {
  pgTable, uuid, text, integer, timestamp, jsonb, boolean,
  index, uniqueIndex, numeric,
} from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// AI Agent Definitions (control plane)
// ---------------------------------------------------------------------------

export const aiAgentDefinitions = pgTable(
  "ai_agent_definitions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    agentKey: text("agent_key").notNull(),
    name: text("name").notNull(),
    description: text("description").notNull(),
    department: text("department").notNull(), // crm, email, ads, booking, support, analytics
    systemPromptTemplate: text("system_prompt_template").notNull(),
    modelTier: text("model_tier").default("MEDIUM").notNull(), // SIMPLE, MEDIUM, COMPLEX, REASONING
    maxTokens: integer("max_tokens").default(2000).notNull(),
    temperature: numeric("temperature", { precision: 3, scale: 2 }).default("0.70").notNull(),
    requiredSkills: jsonb("required_skills").$type<string[]>().default([]).notNull(),
    outputFormat: text("output_format").default("text").notNull(), // text, json, markdown, html
    riskClassification: text("risk_classification").default("minimal").notNull(), // minimal, limited, high
    requiresHumanApproval: boolean("requires_human_approval").default(false).notNull(),
    isActive: boolean("is_active").default(true).notNull(),
    version: integer("version").default(1).notNull(),
    metadata: jsonb("metadata").default({}).notNull(),
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", tz).defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("uq_agent_key").on(table.agentKey),
    index("idx_agent_department").on(table.department),
    index("idx_agent_active").on(table.isActive),
  ],
);

// ---------------------------------------------------------------------------
// AI Skill Library (control plane)
// ---------------------------------------------------------------------------

export const aiSkillLibrary = pgTable(
  "ai_skill_library",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    skillKey: text("skill_key").notNull(),
    name: text("name").notNull(),
    description: text("description").notNull(),
    category: text("category").notNull(), // data_retrieval, content_generation, analysis, action
    promptFragment: text("prompt_fragment").notNull(),
    inputSchema: jsonb("input_schema"), // Zod-compatible schema
    outputSchema: jsonb("output_schema"),
    isActive: boolean("is_active").default(true).notNull(),
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("uq_skill_key").on(table.skillKey),
    index("idx_skill_category").on(table.category),
  ],
);

// ---------------------------------------------------------------------------
// AI Model Routing Config (control plane)
// ---------------------------------------------------------------------------

export const aiModelRoutingConfig = pgTable(
  "ai_model_routing_config",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tier: text("tier").notNull(), // SIMPLE, MEDIUM, COMPLEX, REASONING
    profile: text("profile").default("auto").notNull(), // auto, eco, premium, self_hosted_first
    primaryModel: text("primary_model").notNull(),
    fallbackModels: jsonb("fallback_models").$type<string[]>().default([]).notNull(),
    isActive: boolean("is_active").default(true).notNull(),
    updatedAt: timestamp("updated_at", tz).defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("uq_routing_tier_profile").on(table.tier, table.profile),
  ],
);

// ---------------------------------------------------------------------------
// AI Entity Relationships (tenant DB)
// ---------------------------------------------------------------------------

export const aiEntityRelationships = pgTable(
  "ai_entity_relationships",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    sourceEntityType: text("source_entity_type").notNull(), // contact, deal, company
    sourceEntityId: uuid("source_entity_id").notNull(),
    targetEntityType: text("target_entity_type").notNull(),
    targetEntityId: uuid("target_entity_id").notNull(),
    relationshipType: text("relationship_type").notNull(), // works_at, knows, referred_by, purchased
    confidence: numeric("confidence", { precision: 3, scale: 2 }).default("1.00").notNull(),
    extractedBy: text("extracted_by").default("manual").notNull(), // manual, ai_extraction, import
    metadata: jsonb("metadata").default({}).notNull(),
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
  },
  (table) => [
    index("idx_entity_rel_source").on(table.tenantId, table.sourceEntityType, table.sourceEntityId),
    index("idx_entity_rel_target").on(table.tenantId, table.targetEntityType, table.targetEntityId),
    index("idx_entity_rel_type").on(table.tenantId, table.relationshipType),
  ],
);

// ---------------------------------------------------------------------------
// AI Agent Memory (tenant DB)
// ---------------------------------------------------------------------------

export const aiAgentMemory = pgTable(
  "ai_agent_memory",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    agentKey: text("agent_key").notNull(),
    contextKey: text("context_key").notNull(), // contact:{id}, deal:{id}, global
    memoryType: text("memory_type").default("fact").notNull(), // fact, preference, instruction, conversation_summary
    content: text("content").notNull(),
    importance: integer("importance").default(5).notNull(), // 1-10
    expiresAt: timestamp("expires_at", tz), // null = permanent
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", tz).defaultNow().notNull(),
  },
  (table) => [
    index("idx_agent_memory_lookup").on(table.tenantId, table.agentKey, table.contextKey),
    index("idx_agent_memory_importance").on(table.tenantId, table.importance),
  ],
);

// ---------------------------------------------------------------------------
// Instruction Packs (control plane)
// ---------------------------------------------------------------------------

export const instructionPacks = pgTable(
  "instruction_packs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    packKey: text("pack_key").notNull(),
    name: text("name").notNull(),
    description: text("description").notNull(),
    category: text("category").notNull(), // foundation, output_type, vertical, custom
    version: text("version").default("1.0").notNull(),
    instructions: jsonb("instructions").$type<Array<{ rule: string; priority: number; source: string }>>().notNull(),
    isActive: boolean("is_active").default(true).notNull(),
    isDefault: boolean("is_default").default(false).notNull(),
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", tz).defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("uq_pack_key_version").on(table.packKey, table.version),
    index("idx_pack_category").on(table.category, table.isActive),
  ],
);
